/**
 * HTTP client of the encrypted deferred mailbox (`apps/signaling`, routes
 * `/api/p2p/mailbox/*`). Same origin and same config as signaling
 * (`window.__APP_CONFIG__.p2pSignalingUrl` or the Vite proxy in dev/e2e).
 *
 * - Responses are VALIDATED structurally (no type assertions).
 * - The owner endpoints are SIGNED by the device (ECDSA
 *   P-256 via `signMailboxRequest` from `@enviar/core`); the deposit and the
 *   device listing are open (guest sender, guest-first).
 * - Payloads, keys and signatures are never logged. The envelope payload is
 *   ENCRYPTED end to end: this client only transports it.
 */

import {
  type AuditEntry,
  isRecord,
  parseAuditEntry,
  type RemoteDevice,
  signMailboxRequest,
} from "@enviar/core";

type MailboxErrorKind =
  | "not-found" // unknown or expired account/device/envelope
  | "unauthorized" // invalid signature (or revoked device)
  | "invalid" // quota exceeded or malformed request
  | "rate-limited"
  | "unavailable"; // network down, server not deployed or corrupt response

export class MailboxApiError extends Error {
  override readonly name = "MailboxApiError";
  readonly kind: MailboxErrorKind;

  constructor(kind: MailboxErrorKind) {
    super(`mailbox: ${kind}`);
    this.kind = kind;
  }
}

/** Device credential for the signed endpoints. */
export interface DeviceAuth {
  deviceId: string;
  sigPrivateKey: CryptoKey;
}

function baseUrl(): string {
  // `__APP_CONFIG__` is typed via `declare global { interface Window }` (see
  // signaling-client.ts); `globalThis` does not inherit it (TS7017), so `window`
  // is kept here on purpose.
  // oxlint-disable-next-line unicorn/prefer-global-this
  return `${window.__APP_CONFIG__?.p2pSignalingUrl ?? "/api/p2p"}/mailbox`;
}

function kindForStatus(status: number): MailboxErrorKind {
  if (status === 404) {
    return "not-found";
  }
  if (status === 401) {
    return "unauthorized";
  }
  if (status === 400) {
    return "invalid";
  }
  if (status === 429) {
    return "rate-limited";
  }
  return "unavailable";
}

async function request(
  path: string,
  init: { method: "GET" | "POST" | "DELETE"; body?: string; auth?: DeviceAuth },
): Promise<unknown> {
  const url = new URL(`${baseUrl()}${path}`, globalThis.location.origin);
  const headers = new Headers({ "content-type": "application/json" });
  if (init.auth) {
    // The canonical request signs the PATH as the server will see it.
    const tsSecs = Math.floor(Date.now() / 1000);
    const signature = await signMailboxRequest(init.auth.sigPrivateKey, {
      method: init.method,
      path: url.pathname,
      tsSecs,
      body: init.body === undefined ? undefined : new TextEncoder().encode(init.body),
    });
    headers.set("x-enviar-device", init.auth.deviceId);
    headers.set("x-enviar-ts", String(tsSecs));
    headers.set("x-enviar-sig", signature);
  }
  let response: Response;
  try {
    response = await fetch(url, { method: init.method, headers, body: init.body });
  } catch {
    throw new MailboxApiError("unavailable");
  }
  if (!response.ok) {
    throw new MailboxApiError(kindForStatus(response.status));
  }
  try {
    const body: unknown = await response.json();
    return body;
  } catch {
    throw new MailboxApiError("unavailable");
  }
}

interface RegisteredAccount {
  accountId: string;
  deviceId: string;
}

export async function registerAccount(params: {
  sigPub: string;
  encPub: string;
  deviceName: string;
}): Promise<RegisteredAccount> {
  const body = await request("/accounts", { method: "POST", body: JSON.stringify(params) });
  if (!isRecord(body) || typeof body.accountId !== "string" || typeof body.deviceId !== "string") {
    throw new MailboxApiError("unavailable");
  }
  return { accountId: body.accountId, deviceId: body.deviceId };
}

/** Trusted devices of an account (public: the sender uses it to encrypt). */
export async function fetchDevices(accountId: string): Promise<RemoteDevice[]> {
  const body = await request(`/accounts/${encodeURIComponent(accountId)}/devices`, {
    method: "GET",
  });
  if (!isRecord(body) || !Array.isArray(body.devices)) {
    throw new MailboxApiError("unavailable");
  }
  const devices: RemoteDevice[] = [];
  for (const entry of body.devices) {
    if (
      isRecord(entry) &&
      typeof entry.deviceId === "string" &&
      typeof entry.name === "string" &&
      typeof entry.encPub === "string" &&
      typeof entry.addedAtUnix === "number"
    ) {
      devices.push({
        deviceId: entry.deviceId,
        name: entry.name,
        encPub: entry.encPub,
        addedAtUnix: entry.addedAtUnix,
      });
    }
  }
  return devices;
}

/** Registration of a trusted device, AUTHORIZED by an already registered device. */
export async function addTrustedDevice(
  accountId: string,
  auth: DeviceAuth,
  params: { sigPub: string; encPub: string; deviceName: string },
): Promise<string> {
  const body = await request(`/accounts/${encodeURIComponent(accountId)}/devices`, {
    method: "POST",
    body: JSON.stringify(params),
    auth,
  });
  if (!isRecord(body) || typeof body.deviceId !== "string") {
    throw new MailboxApiError("unavailable");
  }
  return body.deviceId;
}

export async function removeTrustedDevice(
  accountId: string,
  auth: DeviceAuth,
  deviceId: string,
): Promise<void> {
  await request(
    `/accounts/${encodeURIComponent(accountId)}/devices/${encodeURIComponent(deviceId)}`,
    { method: "DELETE", auth },
  );
}

/** Deposit of an ALREADY ENCRYPTED envelope (open: the sender can be a guest). */
export async function depositEnvelope(
  accountId: string,
  toDeviceId: string,
  payload: string,
): Promise<string> {
  const body = await request(`/accounts/${encodeURIComponent(accountId)}/envelopes`, {
    method: "POST",
    body: JSON.stringify({ toDeviceId, payload }),
  });
  if (!isRecord(body) || typeof body.envelopeId !== "string") {
    throw new MailboxApiError("unavailable");
  }
  return body.envelopeId;
}

export interface EnvelopeMeta {
  id: string;
  bytes: number;
  depositedAtUnix: number;
}

export async function listEnvelopes(accountId: string, auth: DeviceAuth): Promise<EnvelopeMeta[]> {
  const body = await request(`/accounts/${encodeURIComponent(accountId)}/envelopes`, {
    method: "GET",
    auth,
  });
  if (!isRecord(body) || !Array.isArray(body.envelopes)) {
    throw new MailboxApiError("unavailable");
  }
  const envelopes: EnvelopeMeta[] = [];
  for (const entry of body.envelopes) {
    if (
      isRecord(entry) &&
      typeof entry.id === "string" &&
      typeof entry.bytes === "number" &&
      typeof entry.depositedAtUnix === "number"
    ) {
      envelopes.push({ id: entry.id, bytes: entry.bytes, depositedAtUnix: entry.depositedAtUnix });
    }
  }
  return envelopes;
}

export async function fetchEnvelope(
  accountId: string,
  auth: DeviceAuth,
  envelopeId: string,
): Promise<string> {
  const body = await request(
    `/accounts/${encodeURIComponent(accountId)}/envelopes/${encodeURIComponent(envelopeId)}`,
    { method: "GET", auth },
  );
  if (!isRecord(body) || typeof body.payload !== "string") {
    throw new MailboxApiError("unavailable");
  }
  return body.payload;
}

/**
 * Account transfer audit (most recent first), SIGNED by
 * a trusted device. Metadata only — the relay is BLIND.
 */
export async function fetchAudit(accountId: string, auth: DeviceAuth): Promise<AuditEntry[]> {
  const body = await request(`/accounts/${encodeURIComponent(accountId)}/audit`, {
    method: "GET",
    auth,
  });
  if (!isRecord(body) || !Array.isArray(body.entries)) {
    throw new MailboxApiError("unavailable");
  }
  const entries: AuditEntry[] = [];
  for (const raw of body.entries) {
    const parsed = parseAuditEntry(raw);
    if (parsed) {
      entries.push(parsed);
    }
  }
  return entries;
}

/** Acknowledgment: deletes the already-picked-up envelope from the mailbox. */
export async function ackEnvelope(
  accountId: string,
  auth: DeviceAuth,
  envelopeId: string,
): Promise<void> {
  await request(
    `/accounts/${encodeURIComponent(accountId)}/envelopes/${encodeURIComponent(envelopeId)}`,
    { method: "DELETE", auth },
  );
}
